import {useMemo} from 'react'

import {
  BankFieldSpecType,
  useGetBankFieldsSchemaQuery,
} from '@progyny/generated/graphql'
import {PaymentFormValues} from '@progyny/pages/Budget/Reimbursement/helpers/PaymentFormModel'
import {parseLocalFields} from '@progyny/pages/Budget/Reimbursement/helpers/util'

type BankFormValidity = {
  isValid: boolean
  schemaLoading: boolean
}

const isNonEmpty = (value: string | undefined): boolean =>
  Boolean(value && value.trim().length > 0)

// Drives the disabled state of the Save / Next buttons. Country-conditional
// fields (`swiftCode` and any schema-driven entries inside `localFields`) are
// validated against the live `bankFieldsSchema` response so the rule set
// matches whatever `CountryBankCodeFields` is rendering.
export const useBankFormValidity = (
  values: Partial<PaymentFormValues> | undefined
): BankFormValidity => {
  const country = values?.country ?? ''
  const {data, loading: schemaLoading} = useGetBankFieldsSchemaQuery({
    variables: {countryCode: country},
    skip: !country,
    fetchPolicy: 'cache-first',
  })

  const localFieldValues = useMemo(
    () => parseLocalFields(values?.localFields),
    [values?.localFields]
  )

  const isValid = useMemo(() => {
    if (!isNonEmpty(values?.country)) return false
    if (!isNonEmpty(values?.accountHolderName)) return false
    if (!isNonEmpty(values?.accountNumber)) return false
    if (!isNonEmpty(values?.bankName)) return false
    if (!isNonEmpty(values?.accountType)) return false

    if (schemaLoading) return false

    const schema = data?.bankFieldsSchema
    const fields: BankFieldSpecType[] = schema?.fields ?? []
    const swiftRequired = schema?.swiftRequired ?? true
    const showSwift = fields.length === 0 || swiftRequired

    if (showSwift && !isNonEmpty(values?.swiftCode)) return false

    for (const field of fields) {
      if (field.required && !isNonEmpty(localFieldValues[field.name])) {
        return false
      }
    }

    return true
  }, [values, schemaLoading, data, localFieldValues])

  return {isValid, schemaLoading}
}
